/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useState, useRef } from "react";

import styled from "styled-components";

import { Layer } from "@vienna/common";

import { Rectangle } from "src/classes/plate/lib/types";
import { HOTKEYS } from "src/components/Hotkeys/hotkeys";
import { borderControl } from "src/components/UI/Menus/utils";
import { useHotkey } from "src/lib/hooks/useHotkey";
import useIsomorphicLayoutEffect from "src/lib/hooks/useIsomorphicLayoutEffect";
import { useOnMouseDownOutside } from "src/lib/hooks/useOnMouseDownOutside";
import { useWindowResize } from "src/lib/hooks/useWindowResize";

interface Props {
  children: React.ReactNode | React.ReactNode[];
  close: () => void;
  anchor?: Rectangle;
  style?: React.CSSProperties;
  blockMouseEvents: boolean;
}

/**
 * Renders children, usually <MenuItem>s, occasionally things like a search bar. Positions self within window.
 */
export function Menu({ children, close, style, anchor, blockMouseEvents }: Props) {
  // positioning
  const menuRef = useRef<HTMLDivElement>(null);
  const [reversed, setReversed] = useState(false);
  const [position, setPosition] = useState<{ top?: number; left?: number; right?: number; bottom?: number }>();
  const [nMenuItemSelected, setNMenuItemSelected] = useState<number | null>(null);

  //closing
  useOnMouseDownOutside(menuRef, close);

  // arrow navigation
  useHotkey(HOTKEYS.ESCAPE, close);

  useHotkey(HOTKEYS.ARROW_UP, () => {
    if (nMenuItemSelected === null) {
      setNMenuItemSelected(0);
      return;
    } else if (nMenuItemSelected > 0) {
      setNMenuItemSelected(nMenuItemSelected - 1);
    }
  });

  useHotkey(HOTKEYS.ARROW_DOWN, () => {
    if (nMenuItemSelected === null) {
      setNMenuItemSelected(0);
      return;
    } else if (nMenuItemSelected < React.Children.count(children) - 1) {
      setNMenuItemSelected(nMenuItemSelected + 1);
    }
  });

  useHotkey(HOTKEYS.ENTER, () => {
    if (nMenuItemSelected !== null) {
      const child = React.Children.toArray(children)[nMenuItemSelected];
      if (React.isValidElement(child)) {
        child.props.handleClick();
      }
    }
  });

  useWindowResize(close);

  // ensure the menu is always on screen
  useIsomorphicLayoutEffect(() => {
    if (!menuRef.current || !menuRef.current.parentElement) return;
    const dimensions = menuRef.current.getBoundingClientRect();
    const { shouldReverse, menuPosition } = borderControl({
      anchor: anchor || menuRef.current.parentElement.getBoundingClientRect(),
      dimensions,
      reversed,
    });
    setReversed(shouldReverse);
    setPosition(menuPosition);
  }, []);

  const childrenWithProps = React.Children.map(children, (child, n) => {
    if (React.isValidElement(child)) {
      //TODO, provide better type than any
      return React.cloneElement<any>(child, {
        isSelectedInMenu: n === nMenuItemSelected,
        selectInMenu: () => setNMenuItemSelected(n),
        deselectInMenu: () => setNMenuItemSelected(null),
      });
    }
    return child;
  });

  return (
    <>
      <MenuWrapper
        onClick={close}
        style={{
          ...position,
          ...style,
        }}
        ref={menuRef}
      >
        {childrenWithProps}
      </MenuWrapper>
      {blockMouseEvents && <ClickInterceptor onContextMenu={(e) => e.preventDefault()} onMouseDown={close} />}
    </>
  );
}

const MenuWrapper = styled.div`
  position: fixed;
  z-index: ${Layer.WIN_LAYER};
  background-color: ${({ theme }) => theme.background};
  font-family: "Inter";
  font-size: 13px;
  color: ${({ theme }) => theme.primary};
  max-width: 285px;
  min-width: 175px;
  border: ${({ theme }) => `1px solid ${theme.primary}`};
  display: flex;
  flex-direction: column;
`;

export const ClickInterceptor = styled.div`
  z-index: ${Layer.WIN_LAYER} - 1; // this is the only place we do z-index math. indicative of this being a weird way to solve the problem it solves
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
`;
